/**
 * Reusable UI feedback utilities: animations, error flashes, skeleton helpers.
 */

import { ANIM_MS } from '../shared/timing.js';

let _reducedMotion = null;

export function prefersReducedMotion() {
  if (_reducedMotion === null) {
    _reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      _reducedMotion = e.matches;
    });
  }
  return _reducedMotion;
}

/** Add a fade-slide-in animation class, auto-cleaned on animationend. */
export function animateIn(el) {
  if (prefersReducedMotion()) return;
  el.classList.remove('pb-animate-in');
  // Force reflow so re-adding the class restarts the animation
  void el.offsetWidth;
  el.classList.add('pb-animate-in');
  el.addEventListener('animationend', () => el.classList.remove('pb-animate-in'), { once: true });
}

/**
 * "It landed here" (PP-0): a single 400ms ring on a freshly created or moved
 * element so the eye finds where the thing went. One keyframe (`pb-landed`)
 * for every list and grid; the canvas keeps its own `flash-section` because
 * that CSS lives inside the iframe. Restartable, self-cleaning, and a no-op
 * under reduced motion — the same contract as animateIn.
 */
export function landed(el) {
  if (!el || prefersReducedMotion()) return;
  el.classList.remove('pb-landed');
  void el.offsetWidth;
  el.classList.add('pb-landed');
  el.addEventListener('animationend', () => el.classList.remove('pb-landed'), { once: true });
}

/** Fade-slide-out animation, returns a promise that resolves when done. */
export function animateOut(el) {
  if (prefersReducedMotion()) return Promise.resolve();
  return new Promise((resolve) => {
    let resolved = false;
    const done = () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(fallback);
      resolve();
    };
    el.classList.add('pb-animate-out');
    el.addEventListener('animationend', done, { once: true });
    const fallback = setTimeout(done, 400);
  });
}

// ── Animation hint queue ──
// Hints are queued by section-manager before state mutations and consumed
// by section-list after re-render to animate the affected row.

const _hints = [];

/** Queue a hint for a section operation. data must include { instanceId, pageId }. */
export function queueHint(type, data) {
  _hints.push({ type, ...data });
}

/** Consume and return hints matching the given pageId. Preserves hints for other pages. */
export function consumeHints(forPageId) {
  const matching = [];
  const remaining = [];
  for (const h of _hints) {
    (h.pageId === forPageId ? matching : remaining).push(h);
  }
  _hints.length = 0;
  _hints.push(...remaining);
  return matching;
}

/**
 * Default anti-flash delay, in ms. Lifted from the canvas render loader, where
 * it was tuned against real renders: below this a loader is perceived as a
 * flicker rather than as feedback, and most local renders finish inside it.
 */
export const LOADER_DELAY_MS = 140;

/**
 * Wrap a loader in the "don't show it if the work finishes fast" rule.
 *
 * This rule existed in exactly one place — a module-local `_loaderDelayTimer` in
 * shell.js — while every other async panel either flashed a spinner for 40ms or
 * showed nothing at all. Showing a loader for work that finishes in 90ms is
 * worse than showing none: the user reads the flicker as a glitch.
 *
 * `stop()` calls `hide()` ONLY if `show()` actually ran. That asymmetry is the
 * whole point — a caller that always paired show/hide would still have to track
 * whether it ever showed, which is the bookkeeping this exists to remove.
 *
 * Re-entrant: `start()` while already armed or already shown is a no-op, so a
 * second call cannot double-arm the timer or re-run `show()`.
 *
 * @param {Object} opts
 * @param {() => void} opts.show
 * @param {() => void} opts.hide
 * @param {number} [opts.delay=140]
 * @returns {{ start: () => void, stop: () => void, isShowing: () => boolean }}
 */
export function createDelayedLoader({ show, hide, delay = LOADER_DELAY_MS } = {}) {
  let timer = null;
  let showing = false;

  function start() {
    if (timer !== null || showing) return;
    timer = setTimeout(() => {
      timer = null;
      showing = true;
      show?.();
    }, delay);
  }

  function stop() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (!showing) return;
    showing = false;
    hide?.();
  }

  return { start, stop, isShowing: () => showing };
}

/** Flash an input red briefly to signal invalid value. */
export function flashError(inputEl, ms = 1500) {
  inputEl.classList.add('pb-input-error');
  setTimeout(() => inputEl.classList.remove('pb-input-error'), ms);
}

/**
 * How many text lines sit under each variant's thumbnail, matched to the real
 * card. Adding a variant means adding a `.pb-skeleton-card-<variant>` aspect
 * ratio in builder.css next to the real card's rule — `skeleton-geometry-tests.js`
 * asserts the pair exists and that the grid templates agree.
 */
const SKELETON_CARD_LINES = { section: 1, template: 3, image: 1, project: 2 };

/**
 * Returns an HTML string of N skeleton placeholder cards.
 *
 * The skeleton has to be the same SHAPE as the card that replaces it, or the
 * swap-in is itself a layout shift — which is the failure the skeleton exists to
 * prevent. So each variant mirrors a real card's geometry: a thumbnail box with
 * that card's aspect ratio, plus the same number of text lines under it. The
 * heights live in CSS (`.pb-skeleton-card-*`) beside the real card's rules.
 *
 * Matching the CARD is necessary but not sufficient — CK-8 found the catalog
 * still reflowed because the skeleton grid's `minmax()` differed from the real
 * grid's, so the two laid out a different number of columns. The skeleton must
 * therefore be injected into the REAL grid container, or into one whose
 * `grid-template-columns` matches it exactly.
 *
 * @param {number} [count=4]
 * @param {{variant?: 'section'|'template'|'image'|'project', itemTag?: 'div'|'li'}} [opts]
 *   `section`  mirrors `.pb-sec-card`          (16/9 thumb + one label line).
 *   `template` mirrors `.pb-template-card`     (16/10 thumb + name + two meta lines).
 *   `image`    mirrors `.pb-image-picker-card` (3/2 thumb + one caption line).
 *   `project`  mirrors `.pb-project-card`      (8/5 thumb + name + meta line).
 *   `itemTag` must be `li` when the real grid is a `<ul>` (the project switcher
 *   is) — `div` children of a list are invalid markup and drop the list
 *   semantics the real grid has.
 */
export function renderSkeletonGrid(count = 4, { variant = 'section', itemTag = 'div' } = {}) {
  const lines = SKELETON_CARD_LINES[variant] ?? 1;
  const tag = itemTag === 'li' ? 'li' : 'div';
  let html = '';
  for (let i = 0; i < count; i++) {
    const body = Array.from(
      { length: lines },
      (_, line) =>
        `<div class="pb-skeleton pb-skeleton-line${line === lines - 1 && lines > 1 ? ' pb-skeleton-line-short' : ''}"></div>`
    ).join('');
    html +=
      `<${tag} class="pb-skeleton-card pb-skeleton-card-${variant}" aria-hidden="true" ` +
      `style="animation-delay:${i * 0.12}s">` +
      `<div class="pb-skeleton pb-skeleton-card-thumb"></div>` +
      `<div class="pb-skeleton-card-lines">${body}</div>` +
      `</${tag}>`;
  }
  return html;
}

/**
 * Attribute marking an <img> whose pixels have not arrived yet. Styled in
 * builder.css with the same pulse as `.pb-skeleton`, so a thumbnail that is
 * still downloading reads as loading rather than as a dead grey box.
 *
 * Stamped by shared/thumbnail-url.js when it wires an image; cleared here.
 */
export const IMAGE_LOADING_ATTR = 'data-pb-thumb-loading';

/**
 * Clear the loading shimmer as each image under `root` finishes.
 *
 * One capture-phase listener for the whole container — `load` does not bubble,
 * and per-image handlers on a 194-card grid is exactly the pattern this codebase
 * has been removing. Errors are deliberately NOT cleared here: an image that
 * 404s is still mid-ladder (see thumbnail-url.js) and should keep shimmering
 * until its last candidate fails, at which point the caller's give-up step runs.
 *
 * @param {Element} root
 * @returns {() => void} teardown
 */
export function installImageSkeletons(root) {
  if (!root) return () => {};
  const onLoad = (event) => {
    const img = event.target;
    if (img instanceof HTMLImageElement && img.hasAttribute(IMAGE_LOADING_ATTR)) {
      img.removeAttribute(IMAGE_LOADING_ATTR);
    }
  };
  root.addEventListener('load', onLoad, true);
  return () => root.removeEventListener('load', onLoad, true);
}

/**
 * Returns an HTML string of N skeleton placeholder ROWS, for list surfaces
 * where the real content is a stack rather than a grid.
 *
 * @param {number} [count=4]
 * @param {{variant?: 'revision', itemTag?: 'div'|'li'}} [opts]
 *   `revision` mirrors `.pb-rev-item` — same padding and bottom border, with the
 *   name/meta pair on the left and a button-sized block on the right, so the
 *   real rows drop in without moving the modal's scroll height.
 *
 *   The `history` variant retired 2026-08-27 with the Version History modal it
 *   mirrored. Its row (`.pb-history-item`) no longer exists, so the variant
 *   could no longer be held to the anti-flash contract this primitive is FOR:
 *   a skeleton whose real row is gone cannot reserve the right height, it can
 *   only look plausible. The timeline reserves its own.
 */
export function renderSkeletonRows(count = 4, { variant = 'revision', itemTag = 'div' } = {}) {
  const tag = itemTag === 'li' ? 'li' : 'div';
  let html = '';
  for (let i = 0; i < count; i++) {
    html +=
      `<${tag} class="pb-skeleton-row pb-skeleton-row-${variant}" aria-hidden="true" ` +
      `style="animation-delay:${i * 0.12}s">` +
      `<div class="pb-skeleton-row-lines">` +
      `<div class="pb-skeleton pb-skeleton-line"></div>` +
      `<div class="pb-skeleton pb-skeleton-line pb-skeleton-line-short"></div>` +
      `</div>` +
      `<div class="pb-skeleton pb-skeleton-row-action"></div>` +
      `</${tag}>`;
  }
  return html;
}
